import { useEffect, useState, useCallback } from "react";
import { useOperator } from "@/context/OperatorContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import CompleteWorkOrderDialog from "@/components/CompleteWorkOrderDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Wrench, PlayCircle, Loader2, Bot, User2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { scheduled: "Scheduled service", battery: "Battery", repair: "Repair", manual: "Manual" };

function CreateWorkOrderDialog({ vehicles, mechanics, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ vehicle_id: "", type: "repair", description: "", assigned_mechanic_id: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.vehicle_id || !form.description.trim()) { toast.error("Pick a vehicle and describe the issue"); return; }
    setSaving(true);
    try {
      await api.post("/operator/work-orders", {
        vehicle_id: form.vehicle_id, type: form.type, description: form.description,
        assigned_mechanic_id: form.assigned_mechanic_id || null,
      });
      toast.success("Work order raised");
      setOpen(false); setForm({ vehicle_id: "", type: "repair", description: "", assigned_mechanic_id: "" });
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="raise-wo-btn" size="sm" className="gap-1.5 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
          <Plus className="h-4 w-4" /> Raise work order
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Raise work order</DialogTitle>
          <DialogDescription>Open a service or repair job and optionally assign a mechanic.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Vehicle</Label>
            <select data-testid="wo-vehicle" value={form.vehicle_id} onChange={(e) => set("vehicle_id", e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select vehicle…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.reg_number} · {v.model} ({v.status})</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label>Type</Label>
            <select data-testid="wo-type" value={form.type} onChange={(e) => set("type", e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="repair">Repair</option>
              <option value="scheduled">Scheduled service</option>
              <option value="battery">Battery</option>
            </select>
          </div>
          <div className="space-y-1.5"><Label>Description</Label>
            <Input data-testid="wo-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Front brake noise" /></div>
          <div className="space-y-1.5"><Label>Assign mechanic (optional)</Label>
            <select data-testid="wo-assign" value={form.assigned_mechanic_id} onChange={(e) => set("assigned_mechanic_id", e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Unassigned</option>
              {mechanics.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="wo-create-submit" onClick={submit} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Raise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WorkOrderCard({ wo, mechanics, onChange }) {
  const assign = async (mechanicId) => {
    try { await api.patch(`/operator/work-orders/${wo.id}/assign`, { mechanic_id: mechanicId }); toast.success("Mechanic assigned"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const start = async () => {
    try { await api.post(`/operator/work-orders/${wo.id}/start`); toast.success("Work started"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`wo-card-${wo.id}`} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold">{wo.vehicle_reg}</p>
          <p className="text-sm text-muted-foreground">{wo.description}</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
          {wo.source === "auto" ? <Bot className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
          {TYPE_LABEL[wo.type] || wo.type}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <User2 className="h-3.5 w-3.5" />
        {wo.assigned_mechanic_name || "Unassigned"}
        {wo.status === "done" && <span className="ml-auto font-medium text-status-available">₹{wo.total_cost} · closed</span>}
      </div>

      {wo.status !== "done" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-tenant-border pt-3">
          <select data-testid={`wo-assign-${wo.id}`} value={wo.assigned_mechanic_id || ""} onChange={(e) => e.target.value && assign(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1 text-xs">
            <option value="">Assign…</option>
            {mechanics.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          {wo.status === "open" && wo.assigned_mechanic_id && (
            <Button data-testid={`wo-start-${wo.id}`} size="sm" variant="outline" className="gap-1.5" onClick={start}>
              <PlayCircle className="h-3.5 w-3.5" /> Start
            </Button>
          )}
          {wo.status === "in_progress" && <CompleteWorkOrderDialog workOrder={wo} onDone={onChange} />}
        </div>
      )}
    </div>
  );
}

export default function Maintenance() {
  const { ctx } = useOperator();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const isStaff = user?.role === "owner" || user?.role === "counter_staff";

  const load = useCallback(async () => {
    const [wo, mech] = await Promise.all([
      api.get("/operator/work-orders"),
      isStaff ? api.get("/operator/mechanics") : Promise.resolve({ data: [] }),
    ]);
    setOrders(wo.data); setMechanics(mech.data); setLoading(false);
  }, [isStaff]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const cols = [
    { key: "open", label: "Open" },
    { key: "in_progress", label: "In progress" },
    { key: "done", label: "Completed" },
  ];
  const byStatus = (k) => orders.filter((o) => o.status === k);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Maintenance</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Work orders are raised manually or auto-scheduled from telemetry (service km / deep discharge). Closing one returns the vehicle to availability.
          </p>
        </div>
        {isStaff && <CreateWorkOrderDialog vehicles={ctx?.vehicles || []} mechanics={mechanics} onCreated={load} />}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {cols.map((c) => (
          <div key={c.key} data-testid={`wo-col-${c.key}`} className="space-y-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              {c.label} <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{byStatus(c.key).length}</span>
            </h2>
            {byStatus(c.key).length === 0 ? (
              <div className="rounded-tenant-card border border-dashed border-tenant-border p-6 text-center text-xs text-muted-foreground">None</div>
            ) : byStatus(c.key).map((wo) => (
              <WorkOrderCard key={wo.id} wo={wo} mechanics={mechanics} onChange={load} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
